import CloseIcon from '@mui/icons-material/Close';
import {
    Button,
    capitalize,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import type { GeneralModalProps } from './types';

const StyDialogActions = styled(DialogActions, { name: 'StyDialogActions' })({
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
});

const GeneralModal = ({
    isOpen,
    title,
    children,
    width = 'sm',
    onClose,
    paperProps = {},
    containedButton = undefined,
    textButton = undefined,
    leftActionButton = undefined,
    customLeftAction = undefined,
    showCloseButton = true,
    shouldCloseOnBackdropClick = false,
    shouldCloseOnEscape = false,
    className = undefined,
}: GeneralModalProps) => {
    const rightActionsButtons = (
        <>
            {textButton && (
                <Button
                    onClick={textButton.onClick}
                    variant="text"
                    className={textButton.className}
                >
                    {capitalize(textButton.text)}
                </Button>
            )}
            {containedButton && (
                <Tooltip title={containedButton.tooltip}>
                    <span>
                        <Button
                            onClick={containedButton.onClick}
                            disabled={containedButton.disabled}
                            loading={containedButton.loading}
                            autoFocus
                            variant="contained"
                            color={containedButton.color}
                            className={containedButton.className}
                        >
                            {capitalize(containedButton.text)}
                        </Button>
                    </span>
                </Tooltip>
            )}
        </>
    );
    const leftActionsButtons = leftActionButton ? (
        <Button
            onClick={leftActionButton.onClick}
            variant={leftActionButton.isDelete ? 'contained' : 'outlined'}
            disabled={leftActionButton.disabled}
            color={leftActionButton.isDelete ? 'error' : 'primary'}
            startIcon={leftActionButton?.startIcon || undefined}
            className={leftActionButton.className}
        >
            {capitalize(leftActionButton.text)}
        </Button>
    ) : (
        customLeftAction
    );
    const dialogActions =
        leftActionButton || customLeftAction ? (
            <StyDialogActions>
                <div>{leftActionsButtons}</div>
                <div style={{ display: 'flex', gap: 8 }}>
                    {rightActionsButtons}
                </div>
            </StyDialogActions>
        ) : (
            <DialogActions>{rightActionsButtons}</DialogActions>
        );
    return (
        <Dialog
            open={isOpen}
            fullScreen={width === 'full-screen'}
            fullWidth
            maxWidth={width === 'full-screen' ? false : width}
            // by default, Dialogs calls onClose when user clicks on backdrop
            // or presses Escape key
            onClose={(_event, reason) => {
                if (reason === 'backdropClick' && shouldCloseOnBackdropClick)
                    onClose();
                if (reason === 'escapeKeyDown' && shouldCloseOnEscape)
                    onClose();
            }}
            className={className}
            slotProps={{ paper: paperProps }}
        >
            {title && <DialogTitle>{title}</DialogTitle>}
            {showCloseButton && (
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={(theme) => ({
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme.palette.darkGrey,
                    })}
                >
                    <CloseIcon />
                </IconButton>
            )}
            <DialogContent>{children}</DialogContent>
            {dialogActions}
        </Dialog>
    );
};

export default GeneralModal;
